// src/features/activity/components/ActivityLog/index.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { getActivityLog } from '../../../../api/activity.api';
import styles from './ActivityLog.module.css';

const ACTION_LABELS = {
  // المهام
  task_created:        { label: 'أضاف مهمة',      color: '#10b981' },
  task_deleted:        { label: 'حذف مهمة',        color: '#ef4444' },
  // الحجوزات
  booking_created:     { label: 'أضاف حجزاً',     color: '#3b82f6' },
  booking_deleted:     { label: 'حذف حجزاً',      color: '#f59e0b' },
  // الدفعات
  payment_created:     { label: 'أضاف دفعة',      color: '#0d9488' },
  payment_deleted:     { label: 'حذف دفعة',       color: '#dc2626' },
  // المصاريف
  expense_created:     { label: 'أضاف مصروفاً',   color: '#7c3aed' },
  expense_deleted:     { label: 'حذف مصروفاً',    color: '#c026d3' },
  // الخزينة
  treasury_deposit:    { label: 'إيداع نقدي',     color: '#059669' },
  treasury_withdrawal: { label: 'سحب نقدي',       color: '#b45309' },
};

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('ar-IQ', { dateStyle: 'short', timeStyle: 'short' });
};

const ActivityLog = () => {
  const [rows,      setRows]      = useState([]);
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [filters,   setFilters]   = useState({
    user_id: '', start_date: '', end_date: '', action_type: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_id)    params.user_id    = filters.user_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date)   params.end_date   = filters.end_date;
      if (filters.action_type) params.action_type = filters.action_type;

      const res = await getActivityLog(params);
      setRows(res.data.results  || []);
      setUsers(res.data.users   || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) =>
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleReset = () =>
    setFilters({ user_id: '', start_date: '', end_date: '', action_type: '' });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>سجل نشاط المستخدمين</h1>
        <p className={styles.sub}>تتبع جميع عمليات الإضافة والحذف</p>
      </div>

      {/* ── الفلاتر ── */}
      <div className={styles.filters}>
        <select name="user_id" value={filters.user_id} onChange={handleChange} className={styles.select}>
          <option value="">كل المستخدمين</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>

        <select name="action_type" value={filters.action_type} onChange={handleChange} className={styles.select}>
          <option value="">كل الإجراءات</option>
          <optgroup label="── المهام ──">
            <option value="task_created">أضاف مهمة</option>
            <option value="task_deleted">حذف مهمة</option>
          </optgroup>
          <optgroup label="── الحجوزات ──">
            <option value="booking_created">أضاف حجزاً</option>
            <option value="booking_deleted">حذف حجزاً</option>
          </optgroup>
          <optgroup label="── الدفعات ──">
            <option value="payment_created">أضاف دفعة</option>
            <option value="payment_deleted">حذف دفعة</option>
          </optgroup>
          <optgroup label="── المصاريف ──">
            <option value="expense_created">أضاف مصروفاً</option>
            <option value="expense_deleted">حذف مصروفاً</option>
          </optgroup>
          <optgroup label="── الخزينة ──">
            <option value="treasury_deposit">إيداع نقدي</option>
            <option value="treasury_withdrawal">سحب نقدي</option>
          </optgroup>
        </select>

        <div className={styles.dateGroup}>
          <label>من</label>
          <input type="date" name="start_date" value={filters.start_date} onChange={handleChange} className={styles.input} />
        </div>

        <div className={styles.dateGroup}>
          <label>إلى</label>
          <input type="date" name="end_date" value={filters.end_date} onChange={handleChange} className={styles.input} />
        </div>

        <button onClick={handleReset} className={styles.resetBtn} type="button">إعادة تعيين</button>
      </div>

      {/* ── الجدول ── */}
      {loading ? (
        <div className={styles.loading}>جاري التحميل...</div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>لا توجد سجلات تطابق الفلاتر المحددة.</div>
      ) : (
        <div className={styles.tableWrap}>
          <p className={styles.count}>إجمالي النتائج: <strong>{rows.length}</strong></p>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>المستخدم</th>
                <th>الإجراء</th>
                <th>التفاصيل</th>
                <th>التاريخ والوقت</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const meta = ACTION_LABELS[row.action_type] || { label: row.action_type, color: '#64748b' };
                return (
                  <tr key={row.id}>
                    <td className={styles.idCell}>{i + 1}</td>
                    <td className={styles.userCell}>{row.user_name}</td>
                    <td>
                      <span
                        className={styles.badge}
                        style={{ background: meta.color }}
                      >
                        {meta.label}
                      </span>
                    </td>
                    <td className={styles.descCell}>{row.description}</td>
                    <td className={styles.timeCell}>{fmt(row.timestamp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
